"use client";

import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import VerticalThumbScroller from "./VerticalThumbScroller";
import { ASSET_PREFIX } from "@/config";
import EnquireNowButton from "./EnquireNowButton";

type GalleryImage = {
  src: string;
  alt: string;
};

type ImageGalleryProps = {
  images?: string[];
  current: number;
  setCurrent: (i: number) => void;
  showOverlayText?: boolean;
};

const defaultImages: GalleryImage[] = [
  {
    src: `${ASSET_PREFIX}/images/pluswilla-working-space.webp`,
    alt: "+Willa Working Space in 4 BHK Villas in Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plusvilla-bedroom.webp`,
    alt: "PlusVilla Bedroom in 3 BHK Villas for sale in Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plusvilla-balcony.webp`,
    alt: "Balcony area in PlusVilla 3 BHK villa Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plusvilla-bedroom-2.webp`,
    alt: "3 BHK villa bedroom in PlusVilla Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plusvilla-dining-hall.webp`,
    alt: "Dining space in 4 BHK PlusVilla in Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plus-willa-working-space.webp`,
    alt: "Working space in PlusVilla TVS Nagar Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/plus-villa-working-area.webp`,
    alt: "Working Area in PlusVilla luxury villas in Coimbatore",
  },
  {
    src: `${ASSET_PREFIX}/images/pluswilla-master-bedroom.webp`,
    alt: "PlusWilla Master Bedroom in 4 BHK Villas",
  },
  {
    src: `${ASSET_PREFIX}/images/modern-gym-plusvilla.webp`,
    alt: "Modern Gym at Gated Community Villas",
  },
  {
    src: `${ASSET_PREFIX}/images/gym-equipments-plus-villa.webp`,
    alt: "Gym Equipments PlusWilla 25",
  },
  {
    src: `${ASSET_PREFIX}/images/indoor-games-plusWilla.webp`,
    alt: "Indoor Games PlusWilla",
  },
  {
    src: `${ASSET_PREFIX}/images/lounge-area-plus-willa.webp`,
    alt: "Lounge Area 3BHK Villas",
  },
  {
    src: `${ASSET_PREFIX}/images/plus-villa-meditation-area.webp`,
    alt: "Plus Villa Meditation Area in Gated Community Villas",
  },
  {
    src: `${ASSET_PREFIX}/images/plusvilla-living-area.webp`,
    alt: "PlusVilla Living Area 3BHK Villas in Coimbatore",
  },
];

export default function ImageGallery({
  images: imagesProp,
  current,
  setCurrent,
  showOverlayText = false,
}: ImageGalleryProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const safeIndexRef = useRef(0);
  const imageLengthRef = useRef(0);
  const activeSrcRef = useRef<string | null>(null);
  const lastImageRef = useRef<GalleryImage | null>(null);
  const fadeTimerRef = useRef<number | undefined>(undefined);
  const rafRef = useRef<number | undefined>(undefined);

  const [galleryReady, setGalleryReady] = useState(false);
  const [previousImage, setPreviousImage] = useState<GalleryImage | null>(null);
  const [currentVisible, setCurrentVisible] = useState(true);

  const images = useMemo(
    () =>
      imagesProp?.length
        ? imagesProp.map((src, i) => ({
            src,
            alt: `Gallery image ${i + 1}`,
          }))
        : defaultImages,
    [imagesProp]
  );

  const safeIndex = useMemo(() => {
    if (!images.length) return 0;
    return Math.min(Math.max(current, 0), images.length - 1);
  }, [current, images.length]);

  const currentImage = images[safeIndex];

  const thumbImages = useMemo(() => images.map((img) => img.src), [images]);
  const thumbAlts = useMemo(() => images.map((img) => img.alt), [images]);

  useEffect(() => {
    safeIndexRef.current = safeIndex;
    imageLengthRef.current = images.length;
  }, [safeIndex, images.length]);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section || !("IntersectionObserver" in window)) {
      setGalleryReady(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setGalleryReady(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "500px 0px",
        threshold: 0.01,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!galleryReady || images.length <= 1) return;

    const timer = window.setInterval(() => {
      if (document.hidden) return;

      const totalImages = imageLengthRef.current;
      if (totalImages <= 1) return;

      const nextIndex = (safeIndexRef.current + 1) % totalImages;
      setCurrent(nextIndex);
    }, 2500);

    return () => window.clearInterval(timer);
  }, [galleryReady, images.length, setCurrent]);

  useEffect(() => {
    if (!currentImage?.src) return;

    if (!activeSrcRef.current) {
      activeSrcRef.current = currentImage.src;
      lastImageRef.current = currentImage;
      setCurrentVisible(true);
      return;
    }

    if (activeSrcRef.current === currentImage.src) return;

    if (lastImageRef.current) {
      setPreviousImage(lastImageRef.current);
    }

    setCurrentVisible(false);

    if (fadeTimerRef.current) {
      window.clearTimeout(fadeTimerRef.current);
    }

    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
    }

    activeSrcRef.current = currentImage.src;
    lastImageRef.current = currentImage;

    rafRef.current = window.requestAnimationFrame(() => {
      setCurrentVisible(true);

      fadeTimerRef.current = window.setTimeout(() => {
        setPreviousImage(null);
      }, 520);
    });
  }, [currentImage]);

  useEffect(() => {
    if (!galleryReady || !images.length) return;

    const nextImage = images[(safeIndex + 1) % images.length];

    if (!nextImage?.src) return;

    let timeoutId: number | undefined;
    let idleId: number | undefined;
    let cancelled = false;

    const preloadNextImage = () => {
      if (cancelled) return;

      const preloadImg = new window.Image();
      preloadImg.decoding = "async";
      preloadImg.src = nextImage.src;
    };

    const idleWindow = window as typeof window & {
      requestIdleCallback?: (
        callback: IdleRequestCallback,
        options?: IdleRequestOptions
      ) => number;
      cancelIdleCallback?: (handle: number) => void;
    };

    if (idleWindow.requestIdleCallback) {
      idleId = idleWindow.requestIdleCallback(preloadNextImage, {
        timeout: 1200,
      });
    } else {
      timeoutId = window.setTimeout(preloadNextImage, 400);
    }

    return () => {
      cancelled = true;

      if (idleId !== undefined && idleWindow.cancelIdleCallback) {
        idleWindow.cancelIdleCallback(idleId);
      }

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [galleryReady, safeIndex, images]);

  useEffect(() => {
    return () => {
      if (fadeTimerRef.current) {
        window.clearTimeout(fadeTimerRef.current);
      }

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  return (
    <>
      <div className="flex justify-between pt-[60px] sm:pt-[80px] md:pt-[100px] lg:pt-[130px] mb-[70px] flex-wrap gap-[20px] w-[85%] mx-auto max-w-[110rem]">
        <h2 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] text-[#000] font-[500] tracking-[-0.03em]">
          Gallery
        </h2>

        <div className="flex items-center flex-wrap gap-[15px]">
          <EnquireNowButton buttonText="Discover Pluswilla" />

          <Link
            href="/gallery"
            aria-label="View all gallery images"
            className="group text-[#000] hover:text-[#fff] bg-[#fff] border border-[#000] rounded-[50px] px-[20px] md:px-[25px] py-[8px] md:py-[12px] w-fit gap-[15px] flex items-center justify-between transition-all duration-300 hover:bg-[#AF8E43] hover:border-[#AF8E43] cursor-pointer"
          >
            View all

            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="transition-transform duration-300 group-hover:rotate-45"
              aria-hidden="true"
            >
              <path
                d="M15.435 1.56497C15.435 1.01269 14.9873 0.564971 14.435 0.564971L5.43503 0.564972C4.88274 0.564971 4.43503 1.01269 4.43503 1.56497C4.43503 2.11726 4.88274 2.56497 5.43503 2.56497L13.435 2.56497L13.435 10.565C13.435 11.1173 13.8827 11.565 14.435 11.565C14.9873 11.565 15.435 11.1173 15.435 10.565L15.435 1.56497ZM1 15L1.70711 15.7071L15.1421 2.27208L14.435 1.56497L13.7279 0.857865L0.292893 14.2929L1 15Z"
                fill="#000"
                className="group-hover:fill-[#fff]"
              />
            </svg>
          </Link>
        </div>
      </div>

      <div
        ref={sectionRef}
        className="flex flex-col md:flex-row justify-center items-center w-full h-full bg-gray-100 relative overflow-hidden max-md:pb-[40px]"
      >
        <div className="relative overflow-hidden shadow-xl w-full flex items-center justify-center">
          <div className="w-full relative">
            {previousImage ? (
              <Image
                src={previousImage.src}
                alt=""
                aria-hidden="true"
                width={1920}
                height={1080}
                sizes="100vw"
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                className={`absolute inset-0 z-[1] w-full h-[350px] sm:h-full object-cover object-bottom transition-opacity duration-500 ease-in-out ${
                  currentVisible ? "opacity-0" : "opacity-100"
                }`}
                draggable={false}
              />
            ) : null}

            {currentImage ? (
              <Image
                key={currentImage.src}
                src={currentImage.src}
                alt={currentImage.alt}
                width={1920}
                height={1080}
                sizes="100vw"
                loading="lazy"
                fetchPriority="low"
                decoding="async"
                className={`relative z-0 w-full h-[350px] sm:h-full object-cover object-bottom transition-opacity duration-500 ease-in-out ${
                  currentVisible ? "opacity-100" : "opacity-0"
                }`}
                draggable={false}
              />
            ) : (
              <div className="w-full h-[350px] sm:h-full bg-neutral-200 animate-pulse" />
            )}

            <div className="absolute inset-0 z-[2] bg-[#06060633]" />

            {showOverlayText && (
              <div className="absolute h-full bottom-0 flex flex-col z-[3] ml-[8%] items-start justify-center left-0 max-sm:pr-[20px]">
                <h2 className="text-[26px] leading-[32px] sm:text-[26px] sm:leading-[32px] md:text-[28px] md:leading-[36px] lg:text-[30px] lg:leading-[38px] xl:text-[32px] xl:leading-[40px] 2xl:text-[35px] 2xl:leading-[42px] text-white font-semibold tracking-[-0.03em] mb-[20px] sm:mb-[25px]">
                  Ready-for-Life <br /> Collection
                </h2>

                <p className="text-white/90 mb-[30px] max-sm:text-[14px] max-sm:leading-[22px]">
                  At +Willa, everything you need is already integrated.
                  <br className="max-sm:hidden" /> No negotiations. No chaos.
                  Just turn the key and start
                  <br className="max-sm:hidden" /> living-sustainably, stress
                  free, and with unmatched long-term savings.
                </p>

                <Link
                  href="#"
                  className="group text-black hover:text-white bg-white rounded-[50px] px-5 md:px-6 py-2.5 md:py-3 w-fit gap-3 flex items-center justify-between transition-all duration-300 hover:bg-[#AF8E43]"
                >
                  View more

                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="transition-transform duration-300 group-hover:rotate-45"
                    aria-hidden="true"
                  >
                    <path
                      d="M15.435 1.56497C15.435 1.01269 14.9873 0.564971 14.435 0.564971L5.43503 0.564972C4.88274 0.564971 4.43503 1.01269 4.43503 1.56497C4.43503 2.11726 4.88274 2.56497 5.43503 2.56497L13.435 2.56497L13.435 10.565C13.435 11.1173 13.8827 11.565 14.435 11.565C14.9873 11.565 15.435 11.1173 15.435 10.565L15.435 1.56497ZM1 15L1.70711 15.7071L15.1421 2.27208L14.435 1.56497L13.7279 0.857865L0.292893 14.2929L1 15Z"
                      className="fill-black group-hover:fill-white"
                    />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>

        <VerticalThumbScroller
          images={thumbImages}
          alts={thumbAlts}
          current={safeIndex}
          onSelect={setCurrent}
          className="static md:absolute md:right-[26px] md:top-1/2 md:-translate-y-1/2"
        />
      </div>
    </>
  );
}